import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Bell, ArrowRight, Building2, Briefcase, Calendar } from "lucide-react";

import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import {
  formatMoney,
  getInvestmentRecordId,
  humanizeStatus,
  INVESTMENT_STATUS_TABS,
  matchesInvestmentPaymentStatus,
} from "lib/investmentListHelpers";
import { parsePortfolioStats } from "lib/portfolioStatsHelpers";
import { PortfolioStatsCards } from "components/investments/PortfolioStatsCards";
import { InvestmentCancellationBound } from "components/investments/InvestmentCancellationBound";
import { InvestmentListRow } from "components/investments/InvestmentListRow";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { selectAuthUser, selectIsAuthenticated } from "store/reducer";

function getActivityRecordId(entry) {
  return entry?._id ?? entry?.id ?? "";
}

function getActivityDisplayDate(entry) {
  const raw =
    entry?.createdAt ??
    entry?.updatedAt ??
    entry?.date ??
    entry?.timestamp ??
    entry?.occurredAt;
  if (!raw) return "";
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function formatActivitySharePart(shares) {
  if (shares == null || shares === "") return "";
  const shareLabel = Number(shares) === 1 ? "share" : "shares";
  return ` · ${shares} ${shareLabel}`;
}

/** Build a line of copy for dashboard feed items (legacy or investment-shaped). */
function getActivityDescription(entry) {
  if (entry?.description && typeof entry.description === "string") {
    return entry.description;
  }
  if (entry?.message && typeof entry.message === "string") {
    return entry.message;
  }
  const title = entry?.project?.title ?? entry?.project?.name;
  const amount = entry?.amount;
  const currency = entry?.currency ?? "USD";
  const shares = entry?.shares;
  const status = humanizeStatus(entry?.status);
  if (title !== null && title !== undefined && amount !== null && amount !== undefined) {
    const money = formatMoney(amount, currency);
    const sharePart = formatActivitySharePart(shares);
    const statusPart = status ? ` · ${status}` : "";
    return `Invested ${money} in ${title}${sharePart}${statusPart}`;
  }
  if (title) return title;
  return "Activity";
}

const AUDIT_ACTION_LABELS = {
  "investment_cancellation_request.created": "Cancellation request submitted",
};

function isAuditLogEntry(entry) {
  return Boolean(entry?.action && entry?.entityType);
}

function humanizeAuditAction(action) {
  if (!action || typeof action !== "string") return "Account activity";
  if (AUDIT_ACTION_LABELS[action]) return AUDIT_ACTION_LABELS[action];
  return action
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getAuditLogDescription(entry) {
  if (entry?.message && typeof entry.message === "string") return entry.message;
  return humanizeAuditAction(entry?.action);
}

function auditLogDotClassName(entry) {
  const action = String(entry?.action ?? "").toLowerCase();
  const status = String(entry?.status ?? "").toLowerCase();
  if (status === "failed" || status === "error") {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-destructive";
  }
  if (action.includes("cancellation")) {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-amber-500";
  }
  if (status === "success") {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-accent";
  }
  return "mt-2 h-2 w-2 shrink-0 rounded-full bg-muted-foreground";
}

function getActivitySortTime(entry) {
  const raw =
    entry?.createdAt ??
    entry?.updatedAt ??
    entry?.date ??
    entry?.timestamp ??
    entry?.occurredAt;
  const time = raw ? new Date(raw).getTime() : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

function activityDotClassName(entry) {
  if (isAuditLogEntry(entry)) return auditLogDotClassName(entry);

  const t = String(entry?.type ?? "").toLowerCase();
  if (
    t === "funded" ||
    entry?.project?.status === "funded" ||
    entry?.status === "funded"
  ) {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-accent";
  }
  if (t === "dividend" || t === "payout") {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-accent";
  }
  if (
    t === "investment" ||
    t === "purchase" ||
    t === "checkout" ||
    (entry?.amount !== null && entry?.amount !== undefined) ||
    (entry?.shares !== null && entry?.shares !== undefined)
  ) {
    return "mt-2 h-2 w-2 shrink-0 rounded-full bg-primary";
  }
  return "mt-2 h-2 w-2 shrink-0 rounded-full bg-muted-foreground";
}

function getUserDisplayName(user) {
  const profile = user?.individualProfile || user?.corporateProfile || {};
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ");

  return (
    name ||
    profile.companyName ||
    user?.name ||
    user?.email?.split("@")?.[0] ||
    "User"
  );
}

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const displayName = useMemo(
    () => (isAuthenticated ? getUserDisplayName(user) : "Guest"),
    [isAuthenticated, user]
  );

  const loadDashboard = useCallback(async (signal) => {
    setError(null);
    const resp = await api.get(API_ENDPOINTS.investmentsDashboard, { signal });
    if (!isSuccessResponse(resp)) {
      const message =
        (resp && typeof resp === "object" && (resp.message || resp.error)) ||
        "Failed to load dashboard";
      throw new Error(message);
    }
    setDashboard(resp?.data ?? resp ?? null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    loadDashboard(controller.signal)
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(e?.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [loadDashboard]);

  const portfolioStats = useMemo(
    () => (dashboard ? parsePortfolioStats(dashboard) : parsePortfolioStats(null)),
    [dashboard]
  );

  const recentTransactions = useMemo(() => {
    const raw =
      dashboard?.recentTransactions?.data ??
      dashboard?.recentTransactions ??
      dashboard?.activity?.data ??
      dashboard?.activity ??
      [];
    return Array.isArray(raw) ? raw : [];
  }, [dashboard]);

  const portfolioCurrency = recentTransactions[0]?.currency ?? "USD";

  const investments = useMemo(() => {
    const raw =
      dashboard?.investments?.data ??
      dashboard?.investments ??
      dashboard?.positions ??
      [];
    return Array.isArray(raw) ? raw : [];
  }, [dashboard]);

  const recentActivity = useMemo(() => {
    const auditLogsRaw =
      dashboard?.recentAuditLogs?.data ?? dashboard?.recentAuditLogs ?? [];
    const auditLogs = Array.isArray(auditLogsRaw) ? auditLogsRaw : [];

    return [...recentTransactions, ...auditLogs].sort(
      (a, b) => getActivitySortTime(b) - getActivitySortTime(a)
    );
  }, [dashboard, recentTransactions]);

  const handleRetry = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    loadDashboard(controller.signal)
      .catch((e) => setError(e?.message || "Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, [loadDashboard]);

  return (
    <div className="bg-secondary/30">
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-12 xl:px-16">
          {/* Welcome Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold">Welcome back, {displayName}!</h1>
              <p className="text-muted-foreground">Here&apos;s an overview of your investment portfolio</p>
            </div>
            <Button asChild className="d-none">
              <Link to={OPPORTUNITIES_PATH}>
                Browse Opportunities
              </Link>
            </Button>
          </div>

          {error ? (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Unable to load dashboard</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleRetry} disabled={loading}>
                  {loading ? "Retrying..." : "Retry"}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <PortfolioStatsCards
            loading={loading}
            stats={portfolioStats}
            currency={portfolioCurrency}
            className="mb-8"
          />


          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Investments List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div className="space-y-1.5">
                    <CardTitle>My Investments</CardTitle>
                    <CardDescription>Track the performance of your portfolio</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0" asChild>
                    <Link to="/my-investments">
                      View
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  <InvestmentCancellationBound onSuccess={handleRetry}>
                    {(cancellation) => (
                  <Tabs defaultValue="all">
                    <TabsList className="mb-4 flex h-auto flex-wrap gap-1">
                      {INVESTMENT_STATUS_TABS.map(({ value, label }) => (
                        <TabsTrigger key={value} value={value}>
                          {label}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {INVESTMENT_STATUS_TABS.map(({ value, status }) => (
                      <TabsContent key={value} value={value} className="space-y-4">
                        {loading ? (
                          <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
                            Loading investments...
                          </div>
                        ) : null}
                        {investments
                          .filter((inv) => matchesInvestmentPaymentStatus(inv, status))
                          .map((investment) => (
                            <InvestmentListRow
                              key={getInvestmentRecordId(investment)}
                              investment={investment}
                              tab={value}
                              onRequestCancel={cancellation.beginCancellation}
                            />
                          ))}
                      </TabsContent>
                    ))}
                  </Tabs>
                    )}
                  </InvestmentCancellationBound>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {loading ? (
                      <div className="text-sm text-muted-foreground">Loading activity...</div>
                    ) : null}
                    {!loading && recentActivity.length === 0 && (
                      <p className="text-sm text-muted-foreground">No recent activity yet.</p>
                    )}
                    {recentActivity.map((activity, index) => (
                      <div
                        key={getActivityRecordId(activity) || `activity-${index}`}
                        className="flex gap-3"
                      >
                        <div className={activityDotClassName(activity)} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm">
                            {isAuditLogEntry(activity)
                              ? getAuditLogDescription(activity)
                              : getActivityDescription(activity)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {getActivityDisplayDate(activity) || "—"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    className="d-none w-full justify-start"
                    asChild
                  >
                    <Link to={OPPORTUNITIES_PATH}>
                      <Building2 className="h-4 w-4 mr-2" />
                      Browse Opportunities
                    </Link>
                  </Button>
                  <Button variant="outline" className="d-none w-full justify-start" asChild>
                    <Link to="/portfolio">
                      <Briefcase className="h-4 w-4 mr-2" />
                      View Full Portfolio
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <Link to="/settings">
                      <Calendar className="h-4 w-4 mr-2" />
                      Account Settings
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
  );
}